import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ColorScheme } from '../constants/colors.ts';
import { CardConfig } from '../constants/cards.ts';

interface WelcomeOverlayProps {
  onOpenInvitation: () => void;
  cardConfig: CardConfig;
  colors: ColorScheme;
}

const WelcomeOverlay: React.FC<WelcomeOverlayProps> = ({ onOpenInvitation, cardConfig, colors }) => {
  const { sharedDetails } = cardConfig;
  // Prevent scrolling when overlay is active (including mobile)
  useEffect(() => {
    // Save current styles
    const originalBodyStyle = window.getComputedStyle(document.body).overflow;
    const originalHtmlStyle = window.getComputedStyle(document.documentElement).overflow;
    const originalBodyPosition = document.body.style.position;
    const originalBodyTop = document.body.style.top;
    const originalBodyWidth = document.body.style.width;
    
    // Get current scroll position
    const scrollY = window.scrollY;
    
    // Prevent scrolling on both body and html
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    // Fix body position to prevent iOS Safari bounce
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    
    // Prevent touch events that could cause scrolling
    const preventTouchMove = (e: TouchEvent) => {
      // Allow touch events on the overlay itself and its children
      const target = e.target as Element;
      const overlayElement = document.querySelector('[data-overlay="welcome"]');
      
      if (overlayElement && (overlayElement.contains(target) || overlayElement === target)) {
        return; // Allow touch events within the overlay
      }
      
      e.preventDefault();
    };
    
    // Add touch event listeners
    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    document.addEventListener('touchstart', preventTouchMove, { passive: false });
    
    // Cleanup function
    return () => {
      // Restore original styles
      document.body.style.overflow = originalBodyStyle;
      document.documentElement.style.overflow = originalHtmlStyle;
      document.body.style.position = originalBodyPosition;
      document.body.style.top = originalBodyTop;
      document.body.style.width = originalBodyWidth;
      
      // Remove touch event listeners
      document.removeEventListener('touchmove', preventTouchMove);
      document.removeEventListener('touchstart', preventTouchMove);
      
      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, []);

  return (
    <motion.div 
      data-overlay="welcome"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[10001] flex flex-col items-center justify-between p-0 h-[100lvh] w-full"
      style={{ 
        backgroundColor: colors.primary,
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="flex flex-col items-center justify-center flex-grow w-full px-8 pb-8"
      >
        <div className="text-center space-y-8 max-w-sm mx-auto">
          {/* Decorative top element */}
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.6, duration: 0.6, ease: "easeOut" }}
            className="w-16 h-[1px] mx-auto mb-8"
            style={{ backgroundColor: colors.accent }}
          />
          
          {/* Greeting */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="space-y-2"
          >
            <p className="font-optima text-sm tracking-[0.2em] uppercase" style={{ color: colors.text }}>
              You are invited to SANGEET NIGHT / BOLLYWOOD NIGHT of
            </p>
          </motion.div>

          {/* Bride and Groom Names */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <h1 className="font-carly-names text-4xl leading-tight" style={{ color: colors.text }}>
                {sharedDetails.groomName}
              </h1>
              <p className="font-optima text-lg font-light" style={{ color: colors.accent }}>
                &
              </p>
              <h1 className="font-carly-names text-4xl leading-tight" style={{ color: colors.text }}>
                {sharedDetails.brideName}
              </h1>
            </div>
          </motion.div>

          {/* Date */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
            className="space-y-2"
          >
            <p className="font-optima text-sm tracking-wide font-medium" style={{ color: colors.text }}>
              {cardConfig.weddingDateDisplay}
            </p>
            <p className="font-optima text-xs leading-relaxed" style={{ color: colors.textLight }}>
              {sharedDetails.venueName}
            </p>
          </motion.div>

          {/* Decorative bottom element */}
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 1.4, duration: 0.6, ease: "easeOut" }}
            className="w-16 h-[1px] mx-auto mt-8 mb-8"
            style={{ backgroundColor: colors.accent }}
          />

          {/* Button moved into the content area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="w-full max-w-sm"
          >
            <motion.button
              onClick={onOpenInvitation}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full font-optima font-medium px-12 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl tracking-[0.15em] uppercase border-2"
              style={{ 
                backgroundColor: colors.secondary,
                color: colors.primary,
                borderColor: colors.accent,
                boxShadow: `0 4px 15px ${colors.shadow}`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = colors.secondaryHover;
                e.currentTarget.style.borderColor = colors.accentHover;
                e.currentTarget.style.boxShadow = `0 6px 20px ${colors.shadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.secondary;
                e.currentTarget.style.borderColor = colors.accent;
                e.currentTarget.style.boxShadow = `0 4px 15px ${colors.shadow}`;
              }}
            >
              Open Invitation
            </motion.button>
          </motion.div>
        </div>
      </motion.div>

      {/* Safe area spacing at bottom */}
      <div className="w-full pb-8 sm:pb-16" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }} />
    </motion.div>
  );
};

export default WelcomeOverlay;
